import React, { useEffect, useState } from 'react';
import moment from 'moment';

import { useGetSessionsForCD } from '../../hooks/sessions';

import { HeaderFilters } from '../sub/complete_data/HeaderFilters';
import { SessionListItem } from '../sub/complete_data/SessionListItem';
import { FeedbacksView } from '../sub/complete_data/FeedbacksView';
import { AnalysisView } from '../sub/complete_data/analysis_view/Index';
import { ConversationsView } from '../sub/complete_data/ConversationsView';
// import { DownloadReports } from '../sub/complete_data/DownloadReports';
import { LogoPackage } from '../sub/LogoPackage';

import { ICompleteDataFilters } from '../../types/misc.interface';
import { ESimulationKey } from '../../types/entities.enum';

export const CompleteData = () => {

    const Simulations = [
        { key: ESimulationKey.AUDIT_DEJA_VU, name: 'Audit Déjà Vu' },
        { key: ESimulationKey.URGENT_RISK_REQUEST, name: 'The Urgent Risk Request' },
        { key: ESimulationKey.SOFT_DOWN_CASE, name: 'The Soft Down Case' },
        { key: ESimulationKey.INCOMPLETE_INITIATIVE_SUBMISSION, name: 'The Incomplete Initiative Submission' },
        { key: ESimulationKey.TRUSTED_CUSTOMERS, name: 'Trusted Customers' },
        { key: ESimulationKey.EXTRA_DRAWER_KEYS, name: 'Extra Drawer Keys' },
        { key: ESimulationKey.VETERAN_BRANCH_MANAGER, name: 'The Veteran Branch Manager' },
        { key: ESimulationKey.THE_SLOGAN_BACKLASH, name: 'The Slogan Backlash' },
        { key: ESimulationKey.ROCKSTAR_REALITY_CHECK, name: 'The Toxic Rockstar’s Reality Check' },
    ];

    const [filters, setFilters] = useState<ICompleteDataFilters>({ start_date: '', end_date: '', scores: [], simulation_keys: [] });
    const [feedbackViewData, setFeedbackViewData] = useState<{ sessionId: string } | null>(null);
    const [analysisViewData, setAnalysisViewData] = useState<{ analysisId: number } | null>(null);
    const [conversationsViewData, setConversationsViewData] = useState<{ conversationId: string } | null>(null);

    const { data: sessionsForCD, isLoading: isSessionsForCDLoading } = useGetSessionsForCD(filters);

    useEffect(() => {
        if (!!feedbackViewData || !!analysisViewData || !!conversationsViewData) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        };
    }, [feedbackViewData, analysisViewData, conversationsViewData]);

    return (
        <div className='complete_data__page'>
            <LogoPackage />

            <div className='card card--md mt-2xl p-xl'>
                <div className='complete_data__header'>
                    <div>
                        <p className='text-xl text-bold color--primary'>User Sessions</p>
                        {((!!filters.start_date && !!filters.end_date) || !!filters.scores.length || !!filters.simulation_keys.length) && (
                            <div className='mt-sm'>
                                <ul className='m-none'>
                                    {!!filters.start_date && !!filters.end_date &&
                                        <li className='text-sm color--gray-600'>
                                            Showing sessions with reports generated between <span className='color--gray-900 text-medium'>{moment(filters.start_date).format('MMM DD, YYYY')} to {moment(filters.end_date).format('MMM DD, YYYY')}</span>
                                        </li>
                                    }
                                    {!!filters.scores.length &&
                                        <li className='text-sm color--gray-600'>
                                            Showing sessions with report average scores are:&nbsp;
                                            <span className='color--gray-900 text-medium'>
                                                {[...filters.scores].sort((a, b) => a - b).reduce((acc, curr, idx, arr) => {
                                                    if (idx === 0) return curr.toString();
                                                    if (idx === arr.length - 1) return `${acc} or ${curr}`;
                                                    return `${acc}, ${curr}`;
                                                }, '')}
                                            </span>
                                        </li>
                                    }
                                    {!!filters.simulation_keys.length &&
                                        <li className='text-sm color--gray-600'>
                                            Showing sessions with scenarios:
                                            <ul className='m-none'>
                                                {filters.simulation_keys.map((key) => (
                                                    <li key={key} className='color--gray-900 text-medium'>
                                                        {Simulations.find(simulation => simulation.key === key)?.name}
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                    }
                                </ul>
                            </div>
                        )}
                    </div>
                    <div className='d-flex justify-content-end align-items-center gap-md'>
                        {/* <DownloadReports sessions={sessionsForCD?.sessions ?? []} /> */}
                        <HeaderFilters
                            filters={filters}
                            setFilters={setFilters}
                            simulations={Simulations}
                        />
                    </div>
                </div>
                <div className='complete_data__body'>
                    {isSessionsForCDLoading && (
                        <div className='d-flex flex-column gap-sm justify-content-center align-items-center'>
                            <div className='list-loader' />
                            <p className='text-semibold'>Loading Sessions</p>
                        </div>
                    )}

                    {!isSessionsForCDLoading && !sessionsForCD?.sessions?.length && (
                        <div className='d-flex flex-column gap-sm justify-content-center align-items-center'>
                            <p className='text-semibold'>No Sessions found!</p>
                        </div>
                    )}

                    {!isSessionsForCDLoading && !!sessionsForCD?.sessions?.length && (
                        <div className='complete_data__sessions_list'>
                            {sessionsForCD.sessions.map((session) => (
                                <SessionListItem
                                    key={session.sessionId}
                                    session={session}
                                    setFeedbackViewData={setFeedbackViewData}
                                    setAnalysisViewData={setAnalysisViewData}
                                    setConversationsViewData={setConversationsViewData}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <FeedbacksView
                open={!!feedbackViewData}
                sessionId={feedbackViewData?.sessionId ?? null}
                setFeedbackViewData={setFeedbackViewData}
            />

            <AnalysisView
                open={!!analysisViewData}
                analysisId={analysisViewData?.analysisId ?? null}
                setAnalysisViewData={setAnalysisViewData}
            />

            <ConversationsView
                open={!!conversationsViewData}
                conversationId={conversationsViewData?.conversationId ?? null}
                setConversationsViewData={setConversationsViewData}
            />
        </div>
    );
};